<template>
  <div class="chat-wrapper">
    <div class="chat-container">
      <div class="chat-header">
        <el-button 
          icon="el-icon-arrow-left" 
          circle 
          size="mini"
          @click="handleBack"
          class="back-button"
        ></el-button>
        <h3>AI智能客服</h3>
      </div>
      
      <div class="chat-messages" ref="messagesContainer">
        <div 
          v-for="(message, index) in messages" 
          :key="index" 
          :class="['message', message.type]"
        >
          <div class="message-content" v-html="parseMarkdown(message.content)"></div>
          <div class="message-time">
            {{ formatTime(message.time) }}
          </div>
        </div>
        <div v-if="isLoading" class="loading-indicator">
          <i class="el-icon-loading"></i> AI正在思考中...
        </div>
      </div>
      
      <div class="chat-input">
        <el-input
          v-model="inputMessage"
          placeholder="请输入您的问题..."
          @keyup.enter.native="sendMessage"
          :disabled="isLoading"
        >
          <el-button
            slot="append"
            icon="el-icon-s-promotion"
            @click="sendMessage"
            :loading="isLoading"
          ></el-button>
        </el-input>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';

const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  highlight: function (str, lang) {
    if (lang && hljs.getLanguage(lang)) {
      try {
        return hljs.highlight(str, { language: lang }).value;
      } catch (__) {}
    }
    return '';
  }
});

export default {
  data() {
    return {
      inputMessage: '',
      isLoading: false,
      messages: [
        {
          type: 'received',
          content: '您好！我是AI智能助手，请问有什么可以帮您？',
          time: new Date()
        }
      ],
      apiConfig: {
        url: 'https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions',
        apiKey: 'sk-2705634a860d484dbbe621e6e618bef0',
        model: 'qwen-plus'
      }
    }
  },
  methods: {
    async sendMessage() {
      if (!this.inputMessage.trim() || this.isLoading) return;

      try {
        this.isLoading = true;
        const userMessage = {
          type: 'sent',
          content: this.inputMessage,
          time: new Date()
        };
        this.messages.push(userMessage);

        const response = await axios.post(
          this.apiConfig.url,
          {
            model: this.apiConfig.model,
            messages: [
              {
                role: 'system',
                content: '你是一个专业的编程和IT技术支持助手，回答要使用Markdown格式，代码块需要标注语言类型'
              },
              {
                role: 'user',
                content: this.inputMessage
              }
            ]
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.apiConfig.apiKey}`
            }
          }
        );

        const aiResponse = {
          type: 'received',
          content: response.data.choices[0].message.content,
          time: new Date()
        };
        this.messages.push(aiResponse);

        this.inputMessage = '';
        this.scrollToBottom();
      } catch (error) {
        console.error('API请求失败:', error);
        this.messages.push({
          type: 'received',
          content: `**请求失败**：${error.response?.data?.message || error.message}`,
          time: new Date()
        });
      } finally {
        this.isLoading = false;
        this.scrollToBottom();
      }
    },

    parseMarkdown(content) {
      return md.render(content);
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        container.scrollTop = container.scrollHeight;
      });
    },

    formatTime(time) {
      return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    handleBack() {
      this.$router.go(-1);
    }
  },
  mounted() {
    this.scrollToBottom();
  }
}
</script>

<style scoped>
.chat-wrapper {
  display: flex;
  justify-content: center;
  padding-top: 20px;
  min-height: 100vh;
  background-color: #f0f2f5;
}

.chat-container {
  width: 800px;
  height: 700px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  background-color: #ffffff;
}

.chat-header {
  padding: 20px;
  background-color: #409EFF;
  color: white;
  border-radius: 8px 8px 0 0;
  position: relative;
}

.chat-messages {
  height: calc(100% - 120px);
  padding: 20px;
  overflow-y: auto;
}

.message {
  margin-bottom: 20px;
}

.message.sent {
  text-align: right;
}

.message-content {
  display: inline-block;
  max-width: 80%;
  padding: 12px 20px;
  border-radius: 20px;
  background: #f1f3f5;
  line-height: 1.6;
}

.message.sent .message-content {
  background: #409EFF;
  color: white;
  border-radius: 20px 4px 20px 20px;
}

.message.received .message-content {
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px 20px 20px 20px;
}

.message-time {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
}

.chat-input {
  padding: 20px;
  border-top: 1px solid #ebeef5;
}

.loading-indicator {
  text-align: center;
  color: #666;
  padding: 10px;
  font-size: 14px;
}

/* Markdown样式增强 */
.message-content ::v-deep h1, 
.message-content ::v-deep h2, 
.message-content ::v-deep h3 {
  margin: 1em 0;
  color: #333;
}

.message-content ::v-deep code {
  background-color: #f3f3f3;
  padding: 2px 4px;
  border-radius: 4px;
  font-family: Consolas, Monaco, monospace;
}

.message-content ::v-deep pre {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 15px 0;
}

.message-content ::v-deep pre code {
  background-color: transparent;
  padding: 0;
}

.message-content ::v-deep blockquote {
  border-left: 4px solid #ddd;
  margin: 1em 0;
  padding-left: 1em;
  color: #666;
}

.message-content ::v-deep table {
  border-collapse: collapse;
  margin: 1em 0;
}

.message-content ::v-deep th, 
.message-content ::v-deep td {
  border: 1px solid #ddd;
  padding: 8px;
}

.message-content ::v-deep a {
  color: #409EFF;
  text-decoration: none;
}

.message-content ::v-deep a:hover {
  text-decoration: underline;
}
</style>